import React, { useState, useEffect } from "react";
import axios from "axios";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { CreditCard, User, FileText, LogOut, Calendar, X, XCircle, CheckCircle } from "lucide-react";
import Footer from "../components/Footer";
import OrderHistory from "../components/OrderHistory";
import { isAuthError, redirectToSafeLogin } from "../authHeaders";

/* -------------------------------------------------------------------------- */
/* ✅ Loader Overlay */
/* -------------------------------------------------------------------------- */
function LoaderOverlay({ show }) {
  if (!show) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/80 backdrop-blur-sm">
      <div className="relative w-24 h-24 flex items-center justify-center">
        <div className="absolute inset-0 border-4 border-gray-900 border-t-transparent rounded-full animate-spin-slow"></div>
        <img
          src="https://checkout.wurq.io/img_1.png"
          alt="WurQ Logo"
          className="h-16 w-auto object-contain"
        />
      </div>
    </div>
  );
}
/* -------------------------------------------------------------------------- */
/* ✅ API Base URL */
/* -------------------------------------------------------------------------- */

const BASE_URL = process.env.REACT_APP_BACKEND_URL;
const PROMOKIT_AFFILIATE_URL = process.env.REACT_APP_PROMOKIT_AFFILIATE_URL;

// Tab id → display label (single source of truth for tab naming)
const TAB_LABELS = {
  overview: "Overview",
  invoices: "Invoice History",
  address: "Update Address",
  orders: "Order History",
};

/* -------------------------------------------------------------------------- */
/* ✅ Card Update Form */
/* -------------------------------------------------------------------------- */
function UpdateCardForm({ customerId, subscriptionId, onComplete, onError }){

  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    try {
      const { data } = await axios.post(`${BASE_URL}/api/create-setup-intent`, {
        customerId,
      });
      const clientSecret = data.clientSecret;

      const result = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
        },
      });

      if (result.error) {
        setMessage(result.error.message);
        onError?.(result.error.message);
        setLoading(false);
        return;
      }

      const paymentMethodId = result.setupIntent.payment_method;
      const setupIntentId = result.setupIntent.id;
      await axios.post(`${BASE_URL}/api/update-payment-method`, {
        customerId,
        subscriptionId,
        paymentMethodId,
        setupIntentId,
      });


      setMessage("✅ Payment method updated successfully!");
      onComplete?.();
    } catch (err) {
      // Prefer the friendly, user-safe message returned by the backend
      // (e.g. card declined / insufficient funds) over a generic string.
      const errorMessage =
        err?.response?.data?.error || "Error updating payment method. Please try again.";
      setMessage(errorMessage);
      onError?.(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="border border-gray-200 p-5 rounded-md mt-4 max-w-md max-md:p-4 max-md:max-w-none"
    >
      <CardElement
        options={{
          style: {
            base: {
              fontSize: "16px",
              color: "#32325d",
              "::placeholder": { color: "#a0aec0" },
            },
          },
          hidePostalCode: true, // ✅ hides ZIP/postal input
        }}
        className="border p-3 rounded-md"
      />
      <button
        type="submit"
        disabled={!stripe || loading}
        className="bg-[#da291c] text-white px-4 py-2 rounded-md w-full mt-4 hover:bg-[#b71f14] transition max-md:min-h-[44px]"
      >
        {loading ? "Updating..." : "Save Payment Method"}
      </button>
      {message && (
        <p className="text-sm text-center text-gray-700 mt-2">{message}</p>
      )}
    </form>
  );
}
/* -------------------------------------------------------------------------- */
/* ✅ Per-Subscription Payment Method Section (Overview tab)                  */
/* -------------------------------------------------------------------------- */
/**
 * Rendered inside EVERY subscription card of the Overview tab. Each
 * subscription can belong to a different Stripe customer (one email may own
 * several customer ids), so the update flow is scoped to this subscription's
 * own customer (sub.customer_info.id) and subscription id.
 */
function SubscriptionPaymentSection({ sub, showToast, onComplete }) {
  const [showCardForm, setShowCardForm] = useState(false);
  const customerId = sub.customer_info?.id;
  const pm = sub.payment_method_info;

  return (
    <div className="mt-6 border-t border-gray-200 pt-4">
      <h4 className="text-[16px] font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <CreditCard size={16} /> Payment Method
      </h4>
      {pm?.card ? (
        <>
          <p className="text-[15px] text-gray-700 mb-4">
            {pm.card.brand?.toUpperCase()} •••• {pm.card.last4}{" "}
            <span className="ml-2 text-[14px] text-gray-600">
              Expires {pm.card.exp_month}/{pm.card.exp_year}
            </span>
          </p>
          {!showCardForm && (
            <button
              onClick={() => setShowCardForm(true)}
              className="bg-[#da291c] text-white px-4 py-2 rounded-md text-[14px] hover:bg-[#b71f14] transition max-md:w-full max-md:min-h-[44px]"
            >
              Update Payment Method
            </button>
          )}
        </>
      ) : (
        <>
          <p className="text-[14px] text-gray-500 mb-3">
            No payment method on file for this subscription.
          </p>
          {!showCardForm && (
            <button
              onClick={() => setShowCardForm(true)}
              className="bg-[#da291c] text-white px-4 py-2 rounded-md text-[14px] hover:bg-[#b71f14] transition max-md:w-full max-md:min-h-[44px]"
            >
              Add Payment Method
            </button>
          )}
        </>
      )}
      {showCardForm && customerId && (
        <Elements stripe={stripePromise}>
          <UpdateCardForm
            customerId={customerId}
            subscriptionId={sub.id}
            onComplete={() => {
              setShowCardForm(false);
              onComplete?.();
            }}
            onError={(msg) => showToast?.(msg || "Error updating payment method.", "error")}
          />
        </Elements>
      )}
    </div>
  );
}
/* ------------------------------------------------------------------------------- */
/* ✅ Change Address Form (Google Places Autocomplete) and fornmatting phone number 
/* ------------------------------------------------------------------------------- */
/**
 */
function formatPhoneNumber(value, dial) {
  const raw = String(value || "");
  if (dial !== "1") return raw;

  let digits = raw.replace(/\D/g, "");
  // A pasted "+1 705 905 4567" arrives here as 11 digits starting with 1.
  if (digits.length === 11 && digits.startsWith("1")) digits = digits.slice(1);
  digits = digits.slice(0, 10);

  if (digits.length < 4) return digits;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function ChangeAddressForm({ email, customerId, onComplete }) {
  const [searchInput, setSearchInput] = useState("");
  const [address, setAddress] = useState({
    line1: "",
    line2: "",
    city: "",
    state: "",
    postal_code: "",
    country: "",
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  // Phone is optional. The national number and the country it belongs to are
  // sent separately so the backend can normalise them to E.164 in one place.
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneCountry, setPhoneCountry] = useState("");
  const [dialCodes, setDialCodes] = useState([]);
  // Google Places calls are proxied through the backend so the browser avoids
  // Google's CORS restrictions and the API key stays server-side.
  const PLACES_AUTOCOMPLETE_URL = `${BASE_URL}/api/places/autocomplete`;
  const PLACES_DETAILS_URL = `${BASE_URL}/api/places/details`;

  const [predictions, setPredictions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [placeMeta, setPlaceMeta] = useState({});
  const [zones, setZones] = useState([]);
  const [provinceOverride, setProvinceOverride] = useState("");
  const [errorField, setErrorField] = useState("");
  const [needsStreetNumber, setNeedsStreetNumber] = useState(false);
  const prefilledLine1Ref = React.useRef("");

  const fetchPredictions = async (value) => {
    if (value.length < 3) {
      setPredictions([]);
      setShowDropdown(false);
      setSearchError("");
      return;
    }

    setSearching(true);
    setSearchError("");

    const url = `${PLACES_AUTOCOMPLETE_URL}?input=${encodeURIComponent(
      value
    )}&types=address`;

    try {
      const res = await fetch(url);
      const data = await res.json();

      if (data?.status === "OK") {
        setPredictions(Array.isArray(data.predictions) ? data.predictions : []);
        setShowDropdown(true);
      } else if (data?.status === "ZERO_RESULTS") {
        setPredictions([]);
        setShowDropdown(false);
        setSearchError("No matches found. Please try a different search.");
      } else {
        setPredictions([]);
        setSearchError(
          data?.error ||
            `Autocomplete failed (status: ${data?.status || "unknown"})`
        );
      }
    } catch (err) {
      setPredictions([]);
      setSearchError("Could not reach the server. Please try again.");
    } finally {
      setSearching(false);
    }
  };

  const skipDebounceRef = React.useRef(false);

  const handleSearchChange = (value) => {
    setSearchInput(value);
    setPredictions([]);
  };

  // Debounce the Places autocomplete so we don't hit the API on every keystroke.
  useEffect(() => {
    if (!searchInput) return;

    // Skip the fetch when the input was set programmatically (e.g. selecting
    // a prediction) rather than by typing.
    if (skipDebounceRef.current) {
      skipDebounceRef.current = false;
      return;
    }

    const timer = setTimeout(() => {
      if (searchInput.length >= 3) {
        fetchPredictions(searchInput);
      }
    }, 300); // Waits 300ms after user stops typing

    return () => clearTimeout(timer);
  }, [searchInput]);

  const selectPrediction = async (prediction) => {
    skipDebounceRef.current = true;
    setSearchInput(prediction.description);
    setShowDropdown(false);
    setPredictions([]);
    setSearching(true);
    setSearchError("");

    const url = `${PLACES_DETAILS_URL}?place_id=${encodeURIComponent(
      prediction.place_id
    )}`;

    try {
      const res = await fetch(url);
      const data = await res.json();

      if (data?.status !== "OK" || !data?.result?.address_components) {
        setSearchError(
          data?.error ||
            `Could not load address details (status: ${data?.status || "unknown"})`
        );
        return;
      }

      const ac = data.result.address_components;
      // Return null (not "") so missing components are an unambiguous falsey value
      const find = (t) => ac.find((c) => c.types.includes(t))?.long_name || null;

      const findShort = (t) => ac.find((c) => c.types.includes(t))?.short_name || null;

      const streetNumber = find("street_number");
      const route = find("route");

      setPlaceMeta({
        countryCode: findShort("country"),
        admin1Long: find("administrative_area_level_1"),
        admin1Short: findShort("administrative_area_level_1"),
        admin2Long: find("administrative_area_level_2"),
        admin2Short: findShort("administrative_area_level_2"),
      });
      setZones([]);
      setProvinceOverride("");
      setErrorField("");

      // Without a street number, pre-fill just the street name so the
      // customer only has to add the number (all fields are editable).
      const line1 =
        streetNumber && route
          ? `${streetNumber} ${route}`.trim()
          : find("street_address") || route || find("premise") || "";
      setNeedsStreetNumber(!streetNumber);
      prefilledLine1Ref.current = line1;

      setAddress({
        line1,
        line2: find("subpremise"),
        city:
          find("locality") || find("sublocality_level_1") || find("postal_town"),
        state:
          find("administrative_area_level_1") ||
          find("administrative_area_level_2"),
        postal_code: find("postal_code"),
        country: find("country"),
      });
    } catch (err) {
      setSearchError("Could not load address details.");
    } finally {
      setSearching(false);
    }
  };

  // Country dial codes for the phone selector (static reference data).
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${BASE_URL}/api/dial-codes`)
      .then(({ data }) => {
        if (!cancelled && Array.isArray(data?.dialCodes)) setDialCodes(data.dialCodes);
      })
      .catch(() => {
        // Non-fatal: the number still saves, the backend falls back to the
        // country on the address when no code was picked.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Default the phone country to the country of the address the customer
  // picked, until they choose one themselves.
  useEffect(() => {
    if (!placeMeta.countryCode) return;
    setPhoneCountry((prev) => prev || placeMeta.countryCode);
  }, [placeMeta.countryCode]);

  const phoneDial = dialCodes.find((c) => c.code === phoneCountry)?.dial || "";

  const setField = (field, value) =>
    setAddress((prev) => ({ ...prev, [field]: value }));

  // Manual edits. When the customer changes the state or country by hand,
  // Google's hidden details for the previous selection no longer apply, so
  // drop them — the backend then matches what the customer typed.
  const editField = (field, value) => {
    setField(field, value);
    if (errorField === field || (field === "state" && errorField === "province")) {
      // The customer is fixing the flagged field — clear the highlight and banner
      setErrorField("");
      setErrorMsg("");
    }
    if (field === "state") {
      setPlaceMeta((prev) => ({ countryCode: prev.countryCode }));
      setProvinceOverride("");
    }
    if (field === "country") {
      setPlaceMeta({});
      setZones([]);
      setProvinceOverride("");
    }
    if (field === "line1" && value !== prefilledLine1Ref.current) {
      setNeedsStreetNumber(false);
    }
  };

  // Shared input styling; the field the backend flagged is outlined in red
  const fieldClass = (name) =>
    `w-full border rounded-lg p-3 text-[15px] ${
      errorField === name ? "border-red-500 ring-1 ring-red-500" : "border-gray-300"
    }`;

  const submittingRef = React.useRef(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Synchronous guard: prevents duplicate submissions from rapid clicks
    // (state updates are async, so `disabled={loading}` alone isn't enough).
    if (submittingRef.current) return;
    submittingRef.current = true;
    setLoading(true);
    setMessage("");
    setErrorMsg("");

    // Google Places can return null for components it doesn't have (e.g. no
    // street number) — guard with `|| ""` so .trim() never throws, which
    // previously left the form stuck with no message.
    const hasValue = (v) => String(v || "").trim().length > 0;
    const missing = !hasValue(address.line1)
      ? "line1"
      : !hasValue(address.city)
      ? "city"
      : !hasValue(address.country)
      ? "country"
      : !hasValue(phoneNumber)
      ? "phone"
      : "";
    if (missing) {
      setErrorMsg(
        missing === "line1"
          ? "Please enter your street address (with house / building number) in Address line 1."
          : missing === "city"
          ? "Please enter your city."
          : missing === "country"
          ? "Please enter your country."
          : "Please enter your phone number."
      );
      setErrorField(missing);
      submittingRef.current = false;
      setLoading(false);
      return;
    }
    // Google found the street but not the house number, and the customer
    // hasn't edited line 1 yet — ask them to complete it.
    if (needsStreetNumber && address.line1 === prefilledLine1Ref.current) {
      setErrorMsg("We couldn't find a house / building number for this address. Please add it to Address line 1, then save.");
      setErrorField("line1");
      submittingRef.current = false;
      setLoading(false);
      return;
    }
    // Postal code / state rules differ per country — the backend checks them
    // against Shopify's country data and tells us which field needs attention.
    setErrorField("");

    try {
      const { data } = await axios.post(`${BASE_URL}/api/update-customer-address`, {
        email,
        customerId,
        address: {
          ...address,
          ...placeMeta,
          ...(provinceOverride ? { provinceOverride } : {}),
          phone: phoneNumber.trim(),
          phoneCountryCode: phoneCountry,
        },
      });
      if (data?.success) {
        // Partial-success: profile updated but some orders could not be updated
        const isPartialFailure = /could not be updated/i.test(data?.message || "");
        setMessage(
          data?.message
            ? `${isPartialFailure ? "⚠️" : "✅"} ${data.message}`
            : "✅ Address updated successfully!"
        );
        onComplete?.();
        // ✅ Clear all fields/inputs after a successful save so the form is
        // ready for a fresh search/update.
        setSearchInput("");
        setPredictions([]);
        setShowDropdown(false);
        setSearchError("");
        setAddress({
          line1: "",
          line2: "",
          city: "",
          state: "",
          postal_code: "",
          country: "",
        });
        setPlaceMeta({});
        setZones([]);
        setProvinceOverride("");
        setErrorField("");
        setNeedsStreetNumber(false);
        setPhoneNumber("");
        setPhoneCountry("");
        prefilledLine1Ref.current = "";
      } else {
        setErrorMsg(data?.error || "Failed to update address.");
      }
    } catch (err) {
      // Auth failure (401/403): this child component has no access to the
      // portal's auth state, so safely redirect to the (unauthenticated)
      // login page.
      if (isAuthError(err)) {
        redirectToSafeLogin();
        return;
      }
      const serverMsg = err?.response?.data?.error;
      setErrorMsg(serverMsg || "Error updating address. Please try again.");
      // Highlight the field the backend flagged; show the province dropdown
      // when it couldn't match the state automatically.
      setErrorField(err?.response?.data?.field || "");
      if (Array.isArray(err?.response?.data?.zones)) {
        setZones(err.response.data.zones);
      }
    } finally {
      // Re-enable the button once the request completes (success or error),
      // so the customer can retry if needed.
      submittingRef.current = false;
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="border border-gray-200 p-5 rounded-md mt-4 max-w-md max-md:p-4 max-md:max-w-none"
    >
      <h4 className="text-[16px] font-semibold text-gray-900 mb-3">
        Billing Address
      </h4>

      {(message || errorMsg) && (
        <p
          className={`mt-0 mb-3 px-3 py-2 rounded-md text-[13px] font-bold text-white ${
            message
              ? // Success message: white bold text on green background
                "bg-green-600"
              : // Error/failure message: white bold text on red background
                "bg-red-600"
          }`}
          role="alert"
        >
          {message || errorMsg}
        </p>
      )}

      <label className="block text-[14px] font-medium text-gray-700 mb-2">
        Search for your new address
        <input
          id="new-address-search"
          type="text"
          autoComplete="off"
          value={searchInput}
          onChange={(e) => handleSearchChange(e.target.value)}
          onFocus={() => predictions.length && setShowDropdown(true)}
          placeholder="Start typing your street address..."
          className="mt-1 w-full border border-gray-300 rounded-lg p-3 text-[15px] focus:ring-2 focus:ring-[#da291c] focus:outline-none"
        />
        {searching && (
          <span className="text-[12px] text-gray-500 mt-1 inline-block">
            Searching...
          </span>
        )}
        {searchError && (
          <span className="text-[12px] text-red-500 mt-1 block">
            {searchError}
          </span>
        )}
        {showDropdown && (
          <ul className="mt-2 max-h-56 overflow-auto border border-gray-200 rounded-lg bg-white shadow-lg divide-y divide-gray-100">
            {predictions.map((p) => (
              <li key={p.place_id}>
                <button
                  type="button"
                  onClick={() => selectPrediction(p)}
                  className="w-full text-left px-3 py-2.5 text-[14px] text-gray-800 hover:bg-[#da291c] hover:text-white transition max-md:min-h-[44px]"
                >
                  {p.description}
                </button>
              </li>
            ))}
          </ul>
        )}
      </label>

      <p className="mt-3 text-[13px] text-gray-600">
        Can't find your address, or need to add an apartment / suite number?
        You can type or edit any field below.
      </p>

      <div className="mt-3 grid gap-3">
        <input
          type="text"
          value={address.line1 || ""}
          onChange={(e) => editField("line1", e.target.value)}
          placeholder="Street address (with house / building number)"
          aria-label="Address line 1"
          autoComplete="address-line1"
          className={fieldClass("line1")}
        />
        {needsStreetNumber && (
          <p className="-mt-1 text-[12px] text-amber-700">
            Google didn't return a house / building number — please add it above.
          </p>
        )}
        <input
          type="text"
          value={address.line2 || ""}
          onChange={(e) => editField("line2", e.target.value)}
          placeholder="Apartment, suite, unit, floor (optional)"
          aria-label="Address line 2"
          autoComplete="address-line2"
          className={fieldClass("line2")}
        />
        <div className="flex gap-3 max-md:flex-col">
          <input
            type="text"
            value={address.city || ""}
            onChange={(e) => editField("city", e.target.value)}
            placeholder="City"
            aria-label="City"
            autoComplete="address-level2"
            className={fieldClass("city")}
          />
          {zones.length > 0 ? (
            <select
              aria-label="State / Province"
              value={provinceOverride}
              onChange={(e) => {
                const zone = zones.find((z) => z.code === e.target.value);
                setProvinceOverride(e.target.value);
                setField("state", zone ? zone.name : "");
                setErrorField("");
              }}
              className={`w-full border rounded-lg p-3 text-[15px] bg-white max-md:min-h-[44px] ${
                errorField === "province" ? "border-red-500 ring-1 ring-red-500" : "border-gray-300"
              }`}
            >
              <option value="">Select state / province</option>
              {zones.map((z) => (
                <option key={z.code} value={z.code}>
                  {z.name}
                </option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              value={address.state || ""}
              onChange={(e) => editField("state", e.target.value)}
              placeholder="State / Province"
              aria-label="State / Province"
              autoComplete="address-level1"
              className={fieldClass("province")}
            />
          )}
        </div>
        <div className="flex gap-3 max-md:flex-col">
          <input
            type="text"
            value={address.postal_code || ""}
            onChange={(e) => editField("postal_code", e.target.value)}
            placeholder="Postal / ZIP code"
            aria-label="Postal / ZIP code"
            autoComplete="postal-code"
            className={`w-full border rounded-lg p-3 text-[15px] ${
              errorField === "postal_code" ? "border-red-500 ring-1 ring-red-500" : "border-gray-300"
            }`}
          />
          <input
            type="text"
            value={address.country || ""}
            onChange={(e) => editField("country", e.target.value)}
            placeholder="Country"
            aria-label="Country"
            autoComplete="country-name"
            className={fieldClass("country")}
          />
        </div>
        <div className="flex gap-3 max-md:flex-col">
          <select
            aria-label="Phone country code"
            value={phoneCountry}
            onChange={(e) => {
              const nextCountry = e.target.value;
              const nextDial = dialCodes.find((c) => c.code === nextCountry)?.dial || "";
              setPhoneCountry(nextCountry);
              setPhoneNumber((prev) => formatPhoneNumber(prev, nextDial));
              if (errorField === "phone") {
                setErrorField("");
                setErrorMsg("");
              }
            }}
            className={`w-full md:w-2/5 border rounded-lg p-3 text-[15px] bg-white max-md:min-h-[44px] ${
              errorField === "phone" ? "border-red-500 ring-1 ring-red-500" : "border-gray-300"
            }`}
          >
            <option value="">Country code</option>
            {dialCodes.map((c) => (
              <option key={c.code} value={c.code}>
                {c.name} (+{c.dial})
              </option>
            ))}
          </select>
          <input
            type="tel"
            value={phoneNumber}
            onChange={(e) => {
              setPhoneNumber(formatPhoneNumber(e.target.value, phoneDial));
              if (errorField === "phone") {
                setErrorField("");
                setErrorMsg("");
              }
            }}
            placeholder="Phone number"
            aria-label="Phone number"
            required
            autoComplete="tel-national"
            className={fieldClass("phone")}
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full bg-[#da291c] hover:bg-[#b71f14] text-white py-3 rounded-md font-medium text-[15px] mt-4 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed max-md:min-h-[44px]"
      >
        {loading ? "Saving..." : "Save Address"}
      </button>
    </form>
  );
}
/* -------------------------------------------------------------------------- */
/* ✅ Toast Notification (top-right) */
/* -------------------------------------------------------------------------- */
function Toast({ toast }) {
  if (!toast) return null;
  const isError = toast.type === "error";
  return (
    <div
      className={`fixed top-6 right-6 z-[60] flex items-start gap-3 max-w-sm w-auto max-md:top-4 max-md:left-4 max-md:right-4 max-md:max-w-none px-4 py-3 rounded-lg shadow-lg animate-toast-slide-in border ${
        isError
          ? "bg-white border-red-200"
          : "bg-white border-gray-200"
      }`}
      role="status"
    >
      <span
        className={`mt-0.5 w-2.5 h-2.5 rounded-full shrink-0 ${
          isError ? "bg-[#da291c]" : "bg-green-500"
        }`}
      ></span>
      <p className="text-[14px] text-gray-800 font-medium">{toast.text}</p>
    </div>
  );
}
/* -------------------------------------------------------------------------- */
/* ✅ Stripe Instance */
/* -------------------------------------------------------------------------- */
/* ✅ Stripe Instance */
/* -------------------------------------------------------------------------- */
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

/* -------------------------------------------------------------------------- */
/* ✅ Subscriptions Page */
/* -------------------------------------------------------------------------- */
export default function Subscriptions() {
  const [email, setEmail] = useState(localStorage.getItem("user_email") || "");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [verified, setVerified] = useState(false);

  const [subscriptions, setSubscriptions] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [message, setMessage] = useState("");
  const [showAffiliateSuccessPopup, setShowAffiliateSuccessPopup] = useState(false);
  const [affiliateActivated, setAffiliateActivated] = useState(false);
  const [affiliatePopupType, setAffiliatePopupType] = useState("success");
  const [affiliatePopupMessage, setAffiliatePopupMessage] = useState("");
  const [toast, setToast] = useState(null);
  const toastTimerRef = React.useRef(null);

  const showToast = (text, type = "error") => {
    setToast({ text, type });
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast(null), 3000);
  };

  const checkAffiliateStatus = async (userEmail) => {
    if (!userEmail) {
      setAffiliateActivated(false);
      return;
    }

    try {
      const normalizedEmail = userEmail.trim().toLowerCase();
      const res = await axios.get(`${BASE_URL}/api/affiliate/status`, {
        params: { email: normalizedEmail },
      });

      if (res.data?.success) {
        setAffiliateActivated(Boolean(res.data?.exists));
      } else {
        setAffiliateActivated(false);
      }
    } catch (err) {
      console.error("❌ Error checking affiliate status:", err);
      setAffiliateActivated(false);
    }
  };

  /* -------------------------------------------------------------------------- */
  /* FETCH DATA */
  /* -------------------------------------------------------------------------- */
  const fetchAllData = async (userEmail) => {
  try {
    setLoading(true);
    setError("");

    localStorage.setItem("user_email", userEmail);

    // Fetch subscriptions
    const subRes = await axios.get(`${BASE_URL}/api/subscriptions/by-email/${userEmail}`);
    const subs = subRes.data?.subscriptions || [];
    const customerInfo = subRes.data?.customer || null;

    setSubscriptions(subs);
    setCustomer(customerInfo);
    await checkAffiliateStatus(userEmail);

    // Optionally show message if no subscriptions
    if (!subs.length) {
      setError("You don't have any active subscriptions yet.");
    }

    // Fetch invoices
    const invoiceRes = await axios.get(`${BASE_URL}/api/invoices/by-email/${userEmail}`);
    const invoices = invoiceRes.data?.invoices || [];

    setInvoices(invoices);

    // Optional: show message if no invoices
    if (!invoices.length && !subs.length) {
      setError("No subscriptions or invoices found.");
    }

  } catch (err) {
    console.error("❌ Error fetching data:", err);
    // Auth failure (401/403): return to the login screen in-place — no reload,
    // so there's no risk of an auth-failure refresh loop.
    if (isAuthError(err)) {
      handleLogout();
      setMessage("Your session has expired. Please log in again.");
      return;
    }
    setError("Error fetching membership details.");
  } finally {
    setLoading(false);
  }
};

  /* -------------------------------------------------------------------------- */
  /* AUTO LOGIN IF A PREVIOUS SESSION EXISTS                                    */
  /* -------------------------------------------------------------------------- */
  useEffect(() => {
    // The session itself lives in an HttpOnly cookie we can't inspect; if it
    // has expired, the first API call returns 401 and the catch blocks reset
    // the portal back to the OTP login screen.
    const savedEmail = localStorage.getItem("user_email");
    if (savedEmail) {
      setVerified(true);
      fetchAllData(savedEmail);
    }
  }, []);

  /* -------------------------------------------------------------------------- */
  /* SEND OTP */
  /* -------------------------------------------------------------------------- */
  const handleSendOtp = async () => {
    try {
      if (!email) return showToast("Please enter your email", "error");
      setLoading(true);
      setError("");

      const res = await axios.post(`${BASE_URL}/api/send-otp`, { email });
      if (res.data.success) {
        setOtpSent(true);
        setError(""); // clear errors
        setMessage("✅ OTP sent! Please check your inbox.");
      } else {
        setMessage("");
        setError("Failed to send OTP.");
      }

    } catch (err) {
      console.error("❌ Error sending OTP:", err);
      const serverMsg = err?.response?.data?.error;
      setError(serverMsg || "Error sending OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  /* -------------------------------------------------------------------------- */
  /* VERIFY OTP */
  /* -------------------------------------------------------------------------- */
  const handleVerifyOtp = async () => {
  try {
    if (!otp) return showToast("Please enter your OTP", "error");
    setLoading(true);
    setError("");

    // 🔥 Normalize email BEFORE sending to backend
    const normalizedEmail = email.trim().toLowerCase();

    const res = await axios.post(`${BASE_URL}/api/verify-otp`, { 
      email: normalizedEmail, 
      otp 
    });

    if (res.data.success) {

      // 🔐 The session token arrives as an HttpOnly cookie set by the server —
      // never exposed to JavaScript, so nothing to store here. Persist only
      // the (non-secret) normalized email for auto-login UI convenience.
      localStorage.setItem("user_email", normalizedEmail);

      setVerified(true);
      setMessage("");

      // 🔥 Always fetch using normalized lowercase email
      await fetchAllData(normalizedEmail);

    } else {
      setError(res.data.error || "Invalid OTP");
    }

  } catch (err) {
    console.error("❌ Error verifying OTP:", err);
    setError("Error verifying OTP.");
  } finally {
    setLoading(false);
  }
};
  /* -------------------------------------------------------------------------- */
  /* BECOME AN AFFILIATE */
  /* -------------------------------------------------------------------------- */
  const handleBecomeAffiliate = async () => {
    try {
      if (affiliateActivated) {
        return;
      }

      setLoading(true);
      const [firstName, ...rest] = (customer?.name || "").split(" ");
      const lastName = rest.join(" ");
      const res = await axios.post(`${BASE_URL}/api/affiliate/info`, {
        email: customer?.email,
        firstName,
        lastName,
      });
      if (res.data.success) {
        setAffiliateActivated(true);
        setAffiliatePopupType("success");
        setAffiliatePopupMessage(
          "Congratulations! Your affiliate profile has been activated and you're ready to begin earning rewards."
        );
        setShowAffiliateSuccessPopup(true);
      } else {
        setAffiliatePopupType("error");
        setAffiliatePopupMessage(res.data.error || "Could not retrieve affiliate info.");
        setShowAffiliateSuccessPopup(true);
      }
    } catch (err) {
      if (isAuthError(err)) {
        handleLogout();
        setMessage("Your session has expired. Please log in again.");
        return;
      }
      setAffiliatePopupType("error");
      setAffiliatePopupMessage(
        err?.response?.data?.error || "Error fetching affiliate info."
      );
      setShowAffiliateSuccessPopup(true);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenPromokitDashboard = () => {
    if (!PROMOKIT_AFFILIATE_URL) {
      console.error("REACT_APP_PROMOKIT_AFFILIATE_URL is not configured");
      return;
    }

    window.open(PROMOKIT_AFFILIATE_URL, "_blank", "noopener,noreferrer");
  };

  /* -------------------------------------------------------------------------- */
  /* CANCEL SUBSCRIPTION */
  /* -------------------------------------------------------------------------- */
  const handleCancel = async (subId) => {
    try {
      const confirmCancel = window.confirm(
        "Are you sure you want to cancel your membership?"
      );
      if (!confirmCancel) return;

      setLoading(true);
      const response = await axios.post(
        `${BASE_URL}/api/subscriptions/cancel/${subId}`
      );
      alert("Membership cancelled successfully.");
      await fetchAllData(email);
    } catch (err) {
      if (err.response) {
      } else if (err.request) {
        console.error("❌ [Cancel] No response received (network/server down?):", err.request);
      } else {
        console.error("❌ [Cancel] Request setup error:", err.message);
      }
      alert(
        "Error cancelling membership." +
          (err.response?.data?.error ? `\nDetails: ${err.response.data.error}` : "")
      );
    } finally {
      setLoading(false);
    }
  };

  /* -------------------------------------------------------------------------- */
  /* LOGOUT */
  /* -------------------------------------------------------------------------- */
  const handleLogout = () => {
    localStorage.removeItem("user_email");
    // Ask the server to clear the HttpOnly session cookie (best-effort)
    axios.post(`${BASE_URL}/api/logout`).catch(() => {});
    setEmail("");
    setOtp("");
    setOtpSent(false);
    setVerified(false);
    setSubscriptions([]);
    setCustomer(null);
    setError("");
    setMessage("");
    setAffiliateActivated(false);
    setAffiliatePopupType("success");
    setAffiliatePopupMessage("");
    setShowAffiliateSuccessPopup(false);
  };

  /* -------------------------------------------------------------------------- */
  /* LOGIN + OTP VIEW */
  /* -------------------------------------------------------------------------- */
  if (!verified) {
    return (
      <div className="min-h-screen flex flex-col md:flex-row bg-gray-50 font-sans relative">
        <LoaderOverlay show={loading} />
        <Toast toast={toast} />

        {/* LEFT SIDE */}
        <div className="md:w-1/2 flex flex-col justify-between p-12 max-md:p-6 border-b md:border-b-0 md:border-r border-gray-200">
          <div>
            <div className="flex items-center mb-6">
              <img
                src="https://checkout.wurq.io/img_1.png"
                alt="WurQ Logo"
                className="w-10 h-10 object-contain mr-3"
              />
              <h1 className="text-[28px] font-bold text-gray-900 tracking-tight">
                WurQ, Inc.
              </h1>
            </div>
            <p className="text-gray-700 text-[17px] leading-relaxed max-w-sm font-medium">
              WurQ, Inc. partners with Stripe for secure billing and simplified
              membership management.
            </p>
          </div>
          <div className="mt-8 text-[13px] text-gray-500">
            Powered by <span className="font-semibold text-gray-700">Stripe</span>
          </div>
        </div>

        {/* RIGHT SIDE */}
        <div className="md:w-1/2 bg-white flex items-center justify-center p-10 max-md:p-6">
          <div className="w-full max-w-sm">
            <h2 className="text-[26px] font-bold text-gray-900 mb-2">
              Manage your membership
            </h2>
            <p className="text-[16px] text-gray-600 mb-6 leading-relaxed">
              Enter your email to receive a one-time login code.
            </p>

            {!otpSent ? (
              <>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="w-full border border-gray-300 rounded-lg p-3 text-[15px] mb-4 focus:ring-2 focus:ring-[#da291c] focus:outline-none"
                />
                <button
                  onClick={handleSendOtp}
                  disabled={loading}
                  className="w-full bg-[#da291c] hover:bg-[#b71f14] text-white py-3 rounded-md font-medium text-[15px] transition-all duration-200"
                >
                  {loading ? "Sending..." : "Send OTP"}
                </button>
                {error && (
                  <p className="text-red-500 mt-3 text-[13px] font-medium">{error}</p>
                )}
              </>
            ) : (
              <>
                <label className="block text-[14px] font-medium text-gray-700 text-left mb-2">
                  Enter the 6-digit code sent to your email
                </label>
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  placeholder="Enter OTP"
                  maxLength={6}
                  className="w-full border border-gray-300 rounded-lg p-3 text-[15px] mb-4 focus:ring-2 focus:ring-[#da291c] focus:outline-none tracking-widest text-center"
                />
                <button
                  onClick={handleVerifyOtp}
                  disabled={loading}
                  className="w-full bg-[#da291c] hover:bg-[#b71f14] text-white py-3 rounded-md font-medium text-[15px] transition-all duration-200"
                >
                  {loading ? "Verifying..." : "Verify & Continue"}
                </button>
                <button
                  onClick={handleSendOtp}
                  disabled={loading}
                  className="w-full mt-3 bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 rounded-md font-medium text-[14px] transition-all duration-200"
                >
                  Resend OTP
                </button>
                {error && (
                  <p className="text-red-500 mt-3 text-[13px] font-medium">{error}</p>
                )}
              </>
            )}

            {message && (
              <p className="text-green-600 mt-4 text-[13px] font-medium">{message}</p>
            )}

          </div>
        </div>
      </div>
    );
  }

  /* -------------------------------------------------------------------------- */
  /* DASHBOARD VIEW */
  /* -------------------------------------------------------------------------- */
  return (
    <div className="h-screen overflow-hidden flex flex-col bg-gray-50 font-sans relative max-md:h-auto max-md:min-h-screen max-md:overflow-visible">
      <LoaderOverlay show={loading} />
      <Toast toast={toast} />
      <div className="flex flex-col md:flex-row flex-1 w-full min-h-0 overflow-hidden max-md:flex-none max-md:overflow-visible">
      <div className="w-full md:w-1/4 bg-gray-50 border-b md:border-b-0 md:border-r border-gray-200 p-10 flex flex-col justify-between overflow-y-auto max-md:px-4 max-md:py-4 max-md:overflow-visible">
        <div>
          <div className="flex items-center space-x-3 mb-5 max-md:mb-2">
            <img
              src="https://checkout.wurq.io/img_1.png"
              alt="Logo"
              className="w-10 h-10 object-contain"
            />
            <h1 className="text-[26px] font-bold text-gray-900 max-md:text-[22px]">WurQ, Inc.</h1>
          </div>
          <p className="text-[16px] text-gray-700 leading-relaxed max-md:text-[14px]">
            Securely powered by Stripe. Manage your membership anytime.
          </p>
        </div>

        <button
          onClick={handleLogout}
          className="mt-10 text-[#da291c] hover:text-[#b71f14] hover:underline text-[14px] font-medium text-left transition flex items-center gap-2 max-md:mt-2 max-md:min-h-[44px] max-md:self-start"
        >
          <LogOut size={16} /> Sign out
        </button>
      </div>

      {/* MAIN CONTENT */}
      <div className="flex-1 p-10 bg-white flex flex-col overflow-hidden max-md:flex-none max-md:px-4 max-md:py-5 max-md:overflow-visible">
        {/* Tabs — sticky header */}
        <div className="shrink-0 flex flex-wrap items-center justify-between border-b mb-8 gap-y-3 max-md:mb-5 max-md:pb-4">
          <div className="flex max-md:grid max-md:grid-cols-2 max-md:gap-2 max-md:w-full">
            {["overview", "invoices", "address", "orders"].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`mr-6 pb-3 text-[16px] font-medium border-b-2 max-md:mr-0 max-md:pb-0 max-md:px-2 max-md:min-h-[44px] max-md:border max-md:rounded-md max-md:text-[14px] max-md:leading-tight ${
                  activeTab === tab
                    ? "border-[#da291c] text-[#da291c] max-md:bg-red-50"
                    : "border-transparent text-gray-500 hover:text-gray-700 max-md:border-gray-200 max-md:bg-white"
                } transition`}
              >
                {TAB_LABELS[tab]}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2 mb-3 max-md:hidden">
            {/* <button
              onClick={handleBecomeAffiliate}
              disabled={loading || affiliateActivated}
              className="bg-[#da291c] text-white px-4 py-2 rounded-md text-[14px] hover:bg-[#b71f14] transition disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading
                ? "Loading..."
                : affiliateActivated
                ? "Affiliate Activated"
                : "Become an Affiliate"}
            </button>

            {affiliateActivated && (
              <button
                type="button"
                onClick={handleOpenPromokitDashboard}
                className="bg-white text-[#da291c] border border-[#da291c] px-4 py-2 rounded-md text-[14px] hover:bg-red-50 transition"
              >
                Promokit Dashboard
              </button>
            )} */}
          </div>
        </div>

        {/* SCROLLABLE TAB CONTENT — scrolls internally, tabs + footer stay fixed */}
        <div className="flex-1 min-h-0 overflow-y-auto max-md:flex-none max-md:overflow-visible">
        {/* OVERVIEW TAB */}
        {activeTab === "overview" && (
          <>
            {subscriptions.length > 0 ? (
              <>
                {subscriptions.map((sub) => {
                  const price = sub.items?.data?.[0]?.price;
                  const product = price?.product;
                  const cancelPending =
                    sub.cancel_at_period_end || sub.status !== "active";
                  const canCancel = sub.can_cancel === true && !cancelPending;
                  const nextBillingDateData =  sub.items?.data?.[0]?.current_period_end;
                  const nextBillingDate =  new Date(nextBillingDateData * 1000);
                   
                  return (
                    <div
                      key={sub.id}
                      className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm mb-8 hover:shadow-md transition-all duration-200 max-md:p-4 max-md:mb-5"
                    >
                      <div className="mb-4">
                        <h3 className="text-[20px] font-semibold text-gray-900 flex items-center gap-2">
                          <User size={18} /> {product?.name || "Membership"}
                        </h3>
                        <p className="text-[16px] text-gray-700 mt-1 font-medium">
                          ${(price?.unit_amount / 100).toFixed(2)} /{" "}
                          {price?.recurring?.interval}
                        </p>
                        {sub.status === "trialing" ? (
                          <p className="text-[13px] text-gray-600 mt-2 italic">
                            Your membership will start once your order gets delivered.
                          </p>
                        ) : sub.status === "canceled" ? (
                          <p className="text-[13px] text-gray-600 mt-2 italic flex items-center gap-2">
                            <XCircle size={14} /> Your subscription has been canceled.
                          </p>
                        ) : nextBillingDateData ? (
                          <p className="text-[13px] text-gray-600 mt-2 italic flex items-center gap-2">
                            <Calendar size={14} /> Next billing date:{" "}
                            {nextBillingDate.toLocaleDateString()}
                          </p>
                        ) : null}
                        {/* Subscription status badge (Active / Canceled) */}
                        <p
                          className={`text-[12px] mt-2 font-semibold inline-flex items-center gap-1 px-2 py-1 rounded-full ${
                            sub.status === "canceled"
                              ? "text-gray-700 bg-gray-100"
                              : "text-green-700 bg-green-50"
                          }`}
                        >
                          {sub.status === "canceled" ? (
                            <>
                              <XCircle size={12} /> Canceled
                            </>
                          ) : (
                            <>
                              <CheckCircle size={12} /> Active
                            </>
                          )}
                        </p>
                      </div>

                {/* BILLING INFO */}
                <div className="mt-6 border-t border-gray-200 pt-4">
                  <h4 className="text-[16px] font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <FileText size={16} /> Billing Information
                  </h4>
                  {sub.customer_info || customer ? (
                    <div className="text-[15px] text-gray-700 space-y-1 max-md:[overflow-wrap:anywhere]">
                      <p>
                        <strong>Name:</strong>{" "}
                        {sub.customer_info?.name || customer?.name || "—"}
                      </p>
                      <p>
                        <strong>Email:</strong>{" "}
                        {sub.customer_info?.email || customer?.email || "—"}
                      </p>
                      {(sub.customer_info?.address || customer?.address) ? (
                        <p>
                          <strong>Billing address:</strong>{" "}
                          {[
                            sub.customer_info?.address?.line1 ?? customer?.address?.line1,
                            sub.customer_info?.address?.city ?? customer?.address?.city,
                            sub.customer_info?.address?.state ?? customer?.address?.state,
                            sub.customer_info?.address?.country ?? customer?.address?.country,
                          ]
                            .filter(Boolean)
                            .join(", ")}
                        </p>
                      ) : (
                        <p className="text-gray-500">No address on file</p>
                      )}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-[14px]">
                      Loading billing info...
                    </p>
                  )}
                </div>

                  {/* PER-SUBSCRIPTION PAYMENT METHOD — scoped to the Stripe
                      customer this subscription belongs to */}
                  <SubscriptionPaymentSection
                    sub={sub}
                    showToast={showToast}
                    onComplete={() => {
                      // Toast first (green), then reload — the loader overlay
                      // renders while fetchAllData refreshes the data.
                      showToast("✅ Payment method updated successfully!", "success");
                      fetchAllData(email);
                    }}
                  />

                      {/* CANCEL BUTTON */}
                      <div className="mt-8 flex justify-end relative max-md:mt-6">
                        <button
                          onClick={canCancel && !cancelPending ? () => handleCancel(sub.id) : undefined}
                          disabled={!canCancel || cancelPending}
                          className={`group px-5 py-2 rounded-md font-medium text-[14px] transition-all relative max-md:w-full max-md:min-h-[44px] ${
                            canCancel && !cancelPending
                              ? "bg-[#da291c] hover:bg-[#b71f14] text-white cursor-pointer"
                              : "bg-gray-200 text-gray-600 cursor-not-allowed"
                          }`}
                        >
                          {cancelPending
                            ? "Cancellation Scheduled"
                            : "Cancel Subscription"}
                          {(!canCancel || cancelPending) && (
                            <div className="absolute bottom-full mb-2 right-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                              <div className="bg-gray-800 text-white text-[12px] rounded-md py-2 px-3 shadow-lg whitespace-nowrap opacity-90 max-md:whitespace-normal max-md:w-64 max-md:text-left">
                                {cancelPending
                                  ? "Your membership is already scheduled to cancel at the end of the current billing period."
                                  : "This option becomes available after your 6 month commitment."}
                                <div className="absolute bottom-[-6px] right-6 w-3 h-3 bg-gray-800 rotate-45"></div>
                              </div>
                            </div>
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })}

            </>
            ) : (
              <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm mb-8 max-md:p-4">
                <p className="text-gray-600 text-[16px]">
                  {error || "You don't have any active subscriptions yet."}
                </p>
              </div>
            )}
          </>
        )}

        {/* INVOICE HISTORY TAB */}
        {activeTab === "invoices" && (
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm max-md:p-4">
            <h3 className="text-[18px] font-semibold text-gray-900 mb-4">
              Invoice History
            </h3>

            {invoices.length > 0 ? (
              <>
              {/* Mobile (<768px): stacked invoice cards instead of a 5-column table */}
              <ul className="md:hidden divide-y divide-gray-200 border-t border-gray-200">
                {invoices.map((inv) => (
                  <li key={inv.id} className="py-3 text-[14px] text-gray-700">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 [overflow-wrap:anywhere]">
                          {inv.line_items?.[0]?.description ||
                            inv.billing_reason ||
                            "—"}
                        </p>
                        <p className="text-[13px] text-gray-500 mt-0.5">
                          {new Date(inv.created * 1000).toLocaleDateString()}
                        </p>
                      </div>
                      <p className="font-semibold text-gray-900 whitespace-nowrap">
                        ${(inv.amount_paid / 100).toFixed(2)}
                      </p>
                    </div>
                    <div className="mt-2 flex items-center justify-between gap-3">
                      {inv.status === "paid" ? (
                        <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-[13px] font-medium">
                          Paid
                        </span>
                      ) : (
                        <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded text-[13px] font-medium capitalize">
                          {inv.status}
                        </span>
                      )}
                      {inv.amount_paid > 0 && inv.invoice_pdf ? (
                        <a
                          href={`${BASE_URL}/api/invoices/download/${inv.id}`}
                          download={`wurq-receipt-${inv.id}.pdf`}
                          className="inline-flex items-center min-h-[44px] px-2 -mr-2 text-[#da291c] hover:underline text-[14px] font-medium"
                        >
                          Download Receipt
                        </a>
                      ) : (
                        <span className="text-gray-500 text-[13px]">
                          Not available
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
              <table className="w-full text-[15px] text-gray-700 max-md:hidden">
                <thead>
                  <tr className="text-left border-b">
                    <th className="py-2">Date</th>
                    <th className="py-2">Description</th>
                    <th className="py-2">Amount</th>
                    <th className="py-2">Status</th>
                    <th className="py-2">Download</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => (
                    <tr key={inv.id} className="border-b hover:bg-gray-50">
                      <td className="py-2">
                        {new Date(inv.created * 1000).toLocaleDateString()}
                      </td>
                      <td className="py-2">
                        {inv.line_items?.[0]?.description ||
                          inv.billing_reason ||
                          "—"}
                      </td>
                      <td className="py-2">
                        ${(inv.amount_paid / 100).toFixed(2)}
                      </td>
                      <td className="py-2 capitalize">
                        {inv.status === "paid" ? (
                          <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-[13px] font-medium">
                            Paid
                          </span>
                        ) : (
                          <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded text-[13px] font-medium">
                            {inv.status}
                          </span>
                        )}
                      </td>
                      <td className="py-2">
                        {inv.amount_paid > 0 && inv.invoice_pdf ? (
                          <a
                            href={`${BASE_URL}/api/invoices/download/${inv.id}`}
                            download={`wurq-receipt-${inv.id}.pdf`}
                            className="text-[#da291c] hover:underline text-[13px]"
                          >
                            Download Receipt
                          </a>
                        ) : (
                          <span className="text-gray-500 text-[13px]">
                            Not available
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </>
            ) : (
              <p className="text-gray-500 text-[14px]">No invoices found.</p>
            )}
          </div>
        )}
{/* CHANGE ADDRESS TAB */}
        {activeTab === "address" && (
          <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm mb-8 max-md:p-4">
            <h3 className="text-[18px] font-semibold text-gray-900 mb-3">
              Update Address
            </h3>
            <p className="text-[14px] text-gray-600 mb-4">
              Search for your new address to fill in the fields automatically,
              or type it in yourself. You can edit any field before saving.
            </p>

            {customer ? (
              <ChangeAddressForm
                email={customer.email}
                customerId={customer.id}
                onComplete={() => fetchAllData(email)}
              />
            ) : (
              <p className="text-gray-500 text-[14px]">
                Loading billing info...
              </p>
            )}
          </div>
        )}

        {/* ORDER HISTORY TAB */}
        {activeTab === "orders" && <OrderHistory email={customer?.email || email} />}
        </div>
      </div>
      </div>

      {/* FOOTER */}
      <Footer />

      {showAffiliateSuccessPopup && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/35 px-4">
          <div className="w-full max-w-md rounded-xl bg-white border border-gray-200 shadow-xl p-5 sm:p-6 relative animate-in fade-in duration-200">
            <button
              type="button"
              aria-label="Close affiliate activation popup"
              onClick={() => setShowAffiliateSuccessPopup(false)}
              className="absolute right-3 top-3 text-gray-500 hover:text-gray-700 transition"
            >
              <X size={18} />
            </button>

            <h3 className="text-[20px] font-semibold text-gray-900 pr-8">
              {affiliatePopupType === "error"
                ? "Affiliate Profile Error"
                : "Affiliate Profile Activated"}
            </h3>
            <p
              className={`mt-3 text-[15px] leading-relaxed ${
                affiliatePopupType === "error" ? "text-red-600" : "text-gray-700"
              }`}
            >
              {affiliatePopupMessage}
            </p>

            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={() => setShowAffiliateSuccessPopup(false)}
                className="bg-[#da291c] text-white px-4 py-2 rounded-md text-[14px] hover:bg-[#b71f14] transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
